import { useQuery, useReactiveVar } from "@apollo/client";
import { PlusIcon } from "@heroicons/react/24/outline";
import { currentDistrictVar } from "apollo/cache/auth";
import {
  HeaderTabs,
  OfficeHeader,
  Shimmers,
  TableComponent,
} from "components";
import { StatusBadge } from "components/core";
import { CEWP_USER_STATUS_BADGE_OPTIONS } from "components/forms/cewp-user/schema";
import ActionButton, { Action } from "components/buttons/action-button";
import config from "config";
import moment from "moment";
import { FC, useEffect, useMemo } from "react";
import { useNavigate, useSearch } from "react-location";
import { LocationGenerics } from "router/location";
import {
  useTableData,
  useUrlState,
  withRoles,
  wrapClick,
} from "utils";
import { GET_CEWP_USERS, GET_CEWP_USERS_SUMMARY } from "./apollo";
import CreateCewpUserContainer from "./create";
import ViewCewpUserContainer from "./view";
import UpdateCewpUserContainer from "./update";
import SuspendCewpUserContainer from "./disable";
import RestoreCewpUserContainer from "./enable";
import DeleteCewpUserContainer from "./delete";

const mainTabs = [
  { name: "Active", href: "Active" },
  { name: "Suspended", href: "Suspended" },
];

const altTabs = [{ name: "Deleted", href: "Deleted" }];

const searchFields = [
  "code",
  "lastName",
  "firstName",
  "phoneNumber",
  "emailAddress",
  "certificationNumber",
];

const CewpUsersPage: FC = () => {
  const searchParams = useSearch<LocationGenerics>();
  const navigate = useNavigate<LocationGenerics>();
  const [modal, setModal] = useUrlState("modal");
  const currentDistrict = useReactiveVar(currentDistrictVar);
  const userStatus = searchParams.userStatus as string | undefined;

  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  const filter = useMemo(() => {
    const status = userStatus || undefined;
    const allowDisabledUsers = !status || status !== "Active";

    return {
      page: Number(searchParams.page || config.constants.page),
      pageSize: Number(searchParams.pageSize || config.constants.pageSize),
      search: (searchParams.search || "").toString(),
      searchFields,
      sort: searchParams.sort || "-createdAt",
      ...(currentDistrict ? { district: currentDistrict } : {}),
      status,
      allowDisabledUsers,
    };
  }, [searchParams, currentDistrict, userStatus]);

  const { data, loading, networkStatus, refetch } = useQuery(GET_CEWP_USERS, {
    variables: filter,
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      refetchSummary?.();
    },
  });

  const { data: dataSummary, refetch: refetchSummary } = useQuery(
    GET_CEWP_USERS_SUMMARY,
    {
      variables: {
        search: filter.search,
        searchFields: filter.searchFields,
        sort: filter.sort,
        district: filter.district,
        allowDisabledUsers: true,
      },
      notifyOnNetworkStatusChange: false,
    },
  );

  useEffect(() => {
    navigate({
      search: (old) => ({
        ...old,
        ...(!old?.page ? { page: config.constants.page } : {}),
        ...(!old?.pageSize ? { pageSize: config.constants.pageSize } : {}),
        ...(!old?.search ? { search: "" } : {}),
        ...(!old?.sort ? { sort: "-createdAt" } : {}),
      }),
    });
  }, [navigate]);

  useEffect(() => {
    navigate({
      search: (old) => ({
        ...old,
        id: undefined,
        modal: undefined,
      }),
    });
  }, [userStatus, navigate]);

  const records = useTableData(data || {});

  return (
    <main className="flex-1 flex flex-col overflow-hidden h-screen bg-gray-50">
      <OfficeHeader
        renderActions={() => (
          <>
            {withRoles(["Super"])(
              <button
                type="button"
                onClick={wrapClick(() => setModal("create"))}
                className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm leading-4 text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
                Add CEWP User
              </button>,
            )}
          </>
        )}
      />
      <HeaderTabs
        mainTabs={mainTabs}
        altTabs={altTabs}
        tabKey="userStatus"
        allLabel="All Users"
        summary={dataSummary?.summary}
      />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 min-h-full mx-auto min-w-0 py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <TableComponent
            title="cewp users"
            refetch={refetch}
            isRefetching={loading && networkStatus === 4}
            loading={loading && ![4, 6].includes(networkStatus)}
            data={records}
            hasSearch={true}
            renderColumns={() => (
              <tr>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Code
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Phone
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Email
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Certification #
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  No. of Districts
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Created At
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                />
              </tr>
            )}
            renderLoader={() => (
              <tr>
                {Array.from({ length: 9 }).map((_, i) => (
                  <td
                    key={i}
                    className="px-6 py-4 border-b border-gray-200"
                  >
                    <Shimmers.SingleShimmer />
                  </td>
                ))}
              </tr>
            )}
            renderItem={(item: any) => {
              return (
                <tr
                  key={item._id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                  onClick={wrapClick(dispatchAction(item._id, "view"))}
                >
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                    <div className="text-gray-900 dark:text-gray-100">
                      {item?.code || "N/A"}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                    <div className="text-gray-900 dark:text-gray-100">
                      {[
                        (item?.lastName || "")?.trim(),
                        (item?.firstName || "")?.trim(),
                      ]
                        .join(" ")
                        .trim() || "N/A"}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                    <div className="text-gray-900 dark:text-gray-100">
                      {item?.phoneNumber || "N/A"}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                    <div className="text-gray-900 dark:text-gray-100">
                      {item?.emailAddress || "N/A"}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                    <div className="text-gray-900 dark:text-gray-100">
                      {item?.certificationNumber || "N/A"}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                    <StatusBadge
                      status={item?.status || ""}
                      options={CEWP_USER_STATUS_BADGE_OPTIONS}
                    />
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 border-b border-gray-200 max-w-xs truncate">
                    <div className="text-gray-900 dark:text-gray-100">
                      {item?.districts?.length || 0}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                    <div className="text-gray-500 dark:text-gray-400">
                      {item?.createdAt
                        ? moment(item.createdAt).format("DD/MM/YYYY HH:mm A")
                        : "N/A"}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200 text-right">
                    <div className="space-x-1" onClick={(e) => e.stopPropagation()}>
                      <ActionButton
                        action="view"
                        onClick={dispatchAction(item._id, "view")}
                      />
                      {withRoles(["Super"])(
                        <>
                          {item?.status === "Active" && (
                            <>
                              <ActionButton
                                action="update"
                                onClick={dispatchAction(item._id, "update")}
                              />
                              <ActionButton
                                action="disable"
                                onClick={dispatchAction(item._id, "disable")}
                              />
                              <ActionButton
                                action="delete"
                                onClick={dispatchAction(item._id, "delete")}
                              />
                            </>
                          )}
                          {item?.status === "Suspended" && (
                            <ActionButton
                              action="enable"
                              onClick={dispatchAction(item._id, "enable")}
                            />
                          )}
                        </>,
                      )}
                    </div>
                  </td>
                </tr>
              );
            }}
          />
        </div>
      </div>

      {withRoles(["Super"])(
        <CreateCewpUserContainer
          open={modal === "create"}
          setOpen={(val: boolean) => setModal(val ? "create" : undefined)}
          refetch={refetch}
        />,
      )}

      {!!searchParams.id?.length && (
        <>
          <ViewCewpUserContainer
            open={modal === "view"}
            setOpen={(val: boolean) => setModal(val ? "view" : undefined)}
          />
          {withRoles(["Super"])(
            <>
              <UpdateCewpUserContainer
                open={modal === "update"}
                setOpen={(val: boolean) => setModal(val ? "update" : undefined)}
                refetch={refetch}
              />
              <SuspendCewpUserContainer
                open={modal === "disable"}
                setOpen={(val: boolean) => setModal(val ? "disable" : undefined)}
                refetch={refetch}
              />
              <RestoreCewpUserContainer
                open={modal === "enable"}
                setOpen={(val: boolean) => setModal(val ? "enable" : undefined)}
                refetch={refetch}
              />
              <DeleteCewpUserContainer
                open={modal === "delete"}
                setOpen={(val: boolean) => setModal(val ? "delete" : undefined)}
                refetch={refetch}
              />
            </>,
          )}
        </>
      )}
    </main>
  );
};

export default CewpUsersPage;
